import { useState } from 'react';
import { Link } from 'react-router';
import type { LiveState } from '../types';

type Level = 'ok' | 'warn' | 'bad';
interface Check {
  level: Level;
  text: string;
  fix?: { to: string; label: string };
}

/** Above this many runners, finishes get dense enough that one timing camera misses people behind others. */
const DENSE_FIELD = 150;

/** What still needs doing before the gun, worked out from the live poll. */
export function setupChecks(raceId: string, live: LiveState): Check[] {
  const { race, timingPoints, stations, counts } = live;
  const course = { to: `/races/${raceId}/course`, label: 'Course & stations' };
  const out: Check[] = [];

  out.push(
    counts.runners > 0
      ? { level: 'ok', text: `${counts.runners} runners in the roster` }
      : { level: 'bad', text: 'No runners imported yet', fix: { to: `/races/${raceId}/roster`, label: 'Import roster' } },
  );

  const finish = timingPoints.find((p) => p.kind === 'finish');
  if (!finish) out.push({ level: 'bad', text: 'No finish timing point', fix: course });

  for (const p of timingPoints) {
    const here = stations.filter((s) => s.timingPointId === p.id);
    const timing = here.filter((s) => s.role !== 'bib');
    const bibs = here.filter((s) => s.role !== 'timing');
    if (timing.length === 0) {
      out.push({ level: p.kind === 'finish' ? 'bad' : 'warn', text: `${p.name}: no timing camera`, fix: course });
    } else if (p.kind === 'finish' && timing.length < 2 && counts.runners > DENSE_FIELD) {
      out.push({ level: 'warn', text: `${p.name}: one timing camera for ${counts.runners} runners. A second, from the other side, catches runners hidden behind others`, fix: course });
    }
    if (bibs.length === 0) out.push({ level: 'warn', text: `${p.name}: no camera reading bibs`, fix: course });
  }

  if (stations.length > 0) {
    const unpaired = stations.filter((s) => !s.paired);
    const offline = stations.filter((s) => s.paired && !s.online);
    const noLine = stations.filter((s) => s.paired && !s.line);
    const badClock = stations.filter((s) => s.online && (s.clockRttMs === null || s.clockRttMs > 400));
    const notRecording = stations.filter((s) => s.online && s.status !== 'recording');
    const lowBattery = stations.filter((s) => s.online && !s.charging && s.battery !== null && s.battery < 0.5);
    const names = (list: typeof stations) => list.map((s) => s.label).join(', ');

    out.push(unpaired.length ? { level: 'bad', text: `Not paired: ${names(unpaired)}`, fix: course } : { level: 'ok', text: `All ${stations.length} phones paired` });
    if (offline.length) out.push({ level: 'bad', text: `Not checking in: ${names(offline)}. Is the page still open on that phone?` });
    if (noLine.length) out.push({ level: 'bad', text: `Line not set: ${names(noLine)}` });
    if (badClock.length) out.push({ level: 'warn', text: `Clock not synced well: ${names(badClock)} (weak signal)` });
    if (lowBattery.length) out.push({ level: 'warn', text: `Battery under 50% and not charging: ${names(lowBattery)}` });
    if (!race.gunTimeMs && notRecording.length === 0 && offline.length === 0 && unpaired.length === 0) out.push({ level: 'ok', text: 'Every phone is recording' });
    else if (notRecording.length) out.push({ level: race.gunTimeMs ? 'bad' : 'warn', text: `Not recording yet: ${names(notRecording)}` });
  }

  if (!race.gunTimeMs) out.push({ level: 'warn', text: 'Gun not fired: press “Fire the gun now” the moment the race starts (you can correct it afterwards)' });
  return out;
}

export function SetupChecklist({ raceId, live }: { raceId: string; live: LiveState }) {
  const checks = setupChecks(raceId, live);
  const problems = checks.filter((c) => c.level !== 'ok');
  const [open, setOpen] = useState<boolean | null>(null);
  // Before the gun it's the main job; afterwards only problems are worth the space.
  const expanded = open ?? (!live.race.gunTimeMs || problems.some((c) => c.level === 'bad'));
  if (live.race.status === 'closed') return null;
  if (live.race.gunTimeMs && problems.length === 0) return null;

  const bad = problems.filter((c) => c.level === 'bad').length;
  return (
    <section className="card stack" style={{ gap: 10 }}>
      <div className="card-head" style={{ margin: 0 }}>
        <h2>{live.race.gunTimeMs ? 'Station problems' : 'Race-day checklist'}</h2>
        <button type="button" className="btn link" onClick={() => setOpen(!expanded)}>
          {expanded ? 'Hide' : bad ? `${bad} to fix` : problems.length ? `${problems.length} to check` : 'All set'}
        </button>
      </div>
      {expanded && (
        <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 6 }}>
          {checks.map((c) => (
            <li key={c.text} className={`notice ${c.level}`} style={{ justifyContent: 'space-between' }}>
              <span>
                {c.level === 'ok' ? '✓ ' : c.level === 'bad' ? '✕ ' : '! '}
                {c.text}
              </span>
              {c.fix && (
                <Link to={c.fix.to} style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {c.fix.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
      {expanded && !live.race.gunTimeMs && (
        <p className="small muted" style={{ margin: 0 }}>
          Volunteers can test their phones ahead of time at <b>{window.location.origin}/station/check</b>. See <Link to="/help">Help</Link> for a printable volunteer card.
        </p>
      )}
    </section>
  );
}
